import React, { useState, Fragment, useRef } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import get from 'lodash/get'

import TradePriceDetails from './TradePriceDetails'
import { useMutation, updateQuery, transformData } from 'components/graphql'
import mutationUpdate from 'gql/mutation/updateSpecialPrice'
import mutationCreate from 'gql/mutation/createSpecialPrice'
import { getProduct } from '../../gql/query'
import { ExpandableBlocks, ContentBlock, ExpandableContext } from 'components/ContentBlock'
import CashregisterView from 'components/CashRegisterTree'
import { Form, Select, Text, Number as NumberInput, DateTimePicker, WeekdayPicker } from 'components/forms'
import { multiValidate, notEmpty, minMaxVal } from 'helpers/validators'
import Button from 'components/Button'
import PriceForm from 'components/forms/fragments/PriceForm'
import Divider from 'components/Divider'
import fetchedDataToForm from 'helpers/fetchedDataToForm'
// import pickerValidate from './helpers/pickerValidate'
import styles from 'style/app.module.scss'
import { loadAppConstants } from 'gql/query'
import weekDaysValidate from './helpers/weekDaysValidate'

import { getSpecialPrice } from 'gql/query'
import { useQuery } from 'components/graphql'

const PriceDetails = ({ productId, priceData = {}, onSubmit }) => {
	const [weekDaysWarning, setWeekDaysWarning] = useState(false)
	const skipQuery = priceData.id < 1
	const { data: queryData, queryField: queryName, loading: queryLoading } = useQuery(getSpecialPrice, {
		variables: { id: priceData.id },
		skip: skipQuery,
		onCompleted: data => {
			const { data: completedData, queryField } = transformData(data)
			const initialFormData = {
				...fetchedDataToForm(completedData[queryField], ['id', 'mod_ver']),
				type: priceData.type
			}
			setFormData(initialFormData)
		}
	})
	const data = skipQuery ? priceData : queryData[queryName]
	const loading = !Object.keys(queryData[queryName] || {}).length && queryLoading

	const allCRIds = useRef([])
	const [t, i18n] = useTranslation()
	const initialFormData = {
		...fetchedDataToForm(data, ['id', 'mod_ver']),
		type: priceData.type
	}
	const [formData, setFormData] = useState(initialFormData)
	const isCreatingNew = priceData.id < 0
	const mutation = isCreatingNew ? mutationCreate : mutationUpdate
	const [mutateSpecialPrice] = useMutation(mutation, { ...isCreatingNew && { onCompleted: () => onSubmit() } })

	const handleSubmit = (vals, mutate) => {
		const isMutationCreate = priceData.id < 0
		const variables = isMutationCreate ?
			{
				required: { productId: productId, type: vals.type, name: vals.name },
				fields: { ...vals, ...!vals.cashRegisterId && { cashRegisterId: allCRIds.current } }
			}
			:
			{
				id: priceData.id,
				fields: vals
			}
		mutateSpecialPrice({
			variables,
			...isMutationCreate && {
				update: updateQuery({ query: getProduct, variables: { id: productId } }, (draft, { mutationResult, queryField }) => {
					const oldPrices = get(draft, [queryField, 'specialPrices'], [])
					oldPrices.push(mutationResult)
				})
			}
		})
	}

	const handleSubmitFailure = (errors, setExpandedId) => {
		const fieldsByBlocks = [
			['name', 'dateInterval', 'timeInterval', 'weekDays'],
			['price', 'priceInCafe', 'codeMWSt', 'codeMWSt2', 'mode', 'actionGroup', 'actionDiscount', 'actionAmount', 'tradePrice'],
			['cashRegisterId']
		]
		const errorField = Object.keys(errors)[0]
		const blockInd = fieldsByBlocks.findIndex(block => block.indexOf(errorField) > -1)
		if(blockInd >= 0) {
			setExpandedId(blockInd.toString())
		}
	}

	const validateWeekDays = vals => {
		const errorMsg = weekDaysValidate(t)(vals)
		if(errorMsg || errorMsg === '') {
			setWeekDaysWarning(errorMsg)
		}
	}

	const priceTranslated = t('product_section.strings.prices')
	const renderOfferType = ({ type, blockCR, formState, formApi }) => {
		switch(type) {
			case 1: return(
				<ContentBlock label={priceTranslated} blockId='1'>
					<PriceForm disabledFields={['codeMWSt', 'codeMWSt2']} priceFieldsProps={{ keepState: true }} enableNegative />
				</ContentBlock>
			)
			case 2: return(
				<Fragment>
					<ContentBlock label={priceTranslated} blockId='1'>
						<PriceForm priceFieldsProps={{ keepState: true }} enableNegative />
					</ContentBlock>
					{blockCR}
				</Fragment>
			)
			case 3: return(
				<Fragment>
					<ContentBlock label={priceTranslated} blockId='1'>
						<PriceForm disabledFields={['codeMWSt', 'codeMWSt2']} priceFieldsProps={{ keepState: true }} enableNegative />
					</ContentBlock>
					{blockCR}
				</Fragment>
			)
			case 4: return(
				<Fragment>
					<ContentBlock label={t('product_section.strings.mode')} blockId='1'>
						<TradePriceDetails mode={get(formState, 'values.mode', 0)} />
					</ContentBlock>
					{blockCR}
				</Fragment>
			)
			case 5: return(
				<Fragment>
					<ContentBlock label={t('product_section.strings.discount')} blockId='1'>
						<div><Text field='mode' initialValue={1} hidden /></div>
						<NumberInput
							field='actionDiscount'
							label={`${t('product_section.strings.discount')}, %`}
							{...multiValidate([notEmpty(), minMaxVal({ max: 99 })])}
							withControls={false} />
					</ContentBlock>
					{blockCR}
				</Fragment>
			)
			default: return null
		}
	}

	if(!queryData) {
		return null
	}

	const validDatePick = (v) => {
		if(v && typeof v === 'string' && v.length) {
			return null
		}
		if(v && typeof v === 'object' && v.from && v.to) {
			return null
		}
		return t('validators.required')
	}
	return(
		<ContentBlock
			label={priceData.typeName || t('product_section.strings.new_price')}
			related
			className={classNames({ [styles.loading]: loading })}
		>
			<ExpandableBlocks nested>
				<ExpandableContext.Consumer>
					{({ setExpandedId }) => (
						<Form
							// key={priceData.id + formData.type}
							// key={formKey}
							key={priceData.id}
							initialValues={formData}
							onSubmitFailure={errors => handleSubmitFailure(errors, setExpandedId)}
							smartSubmit={!isCreatingNew}
							onSubmit={handleSubmit}
							onValueChange={validateWeekDays}
						>
							{({ formState, formApi }) => {
								const offerType = Number(formApi.getValue('type') || formData.type)
								const isSimpleType = offerType === 1 || offerType === 2
								const blockCR = (
									<ContentBlock label={t('common.fields.cash_register')} blockId='2'>
										<div>
											<Text field='cashRegisterId' hidden />
										</div>
										<CashregisterView
											key={formData?.cashRegisterId?.length}
											startAllActive={isCreatingNew}
											activeNodesArr={formData.cashRegisterId}
											onChange={vals => {
												formApi.setValue('cashRegisterId', vals)
												if(!isCreatingNew) {
													formApi.submitForm()
												}
											}}
											{...isCreatingNew && { allValsRef: allCRIds }}
											inverted />
									</ContentBlock>
								)
								return(
									<Fragment>
										<Row>
											<Col xs>
												<Select
													field='type'
													label={t('product_section.strings.type')}
													remoteOptions={{
														query: loadAppConstants,
														variables: { type: 36, language: i18n.language }
													}}
													beforeSetFormValue={value => Number(value)}
												/>
												<Text label={t('common.fields.name')} field='name' {...notEmpty()} validateOnChange />
											</Col>
											<Col xs>
												<Fragment key={isSimpleType}>
													<DateTimePicker
														field={isSimpleType ? 'dateInterval.from' : 'dateInterval'}
														picker='date'
														interval={!isSimpleType}
														label={{
															from: t('common.fields.from_date'),
															to: t('common.fields.to_date')
														}}
														validate={validDatePick}
														validateOnChange
														disablePast />
													{offerType !== 1 &&
                            <DateTimePicker
                            	field={isSimpleType ? 'timeInterval.from' : 'timeInterval'}
                            	picker='time'
                            	interval={!isSimpleType}
                            	label={{
                            		from: t('common.fields.from_time'),
                            		to: t('common.fields.to_time')
                            	}}
                            	validate={validDatePick}
                            	validateOnChange
                            	initialValue={isSimpleType ? priceData?.timeInterval?.from : priceData?.timeInterval}
                            	disablePast />
													}
												</Fragment>
											</Col>
											<Col xs>
												{!isSimpleType &&
                          <WeekdayPicker {...skipQuery && { initialValue: 127 }} field='weekDays' warning={weekDaysWarning} />
												}
											</Col>
										</Row>
										{renderOfferType({ type: offerType, blockCR, formState, formApi })}
										{isCreatingNew &&
                      <Fragment>
                      	<Divider height={20}/>
                      	<div>
                      		<Button status='primary' text={t('common.buttons.apply')} type='submit' />
                      	</div>
                      </Fragment>
										}
									</Fragment>
								)
							}}
						</Form>
					)}
				</ExpandableContext.Consumer>
			</ExpandableBlocks>
		</ContentBlock>
	)
}

export default PriceDetails
// compose(
//  branch(({ priceData }) => priceData.id > 0,
//      withQuery
//  )
// )(PriceDetails)

